import axios from 'axios';
import { isAuthRejection } from './auth-errors';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000/api/v1';

/**
 * Instância Axios configurada
 */
const api = axios.create({
  baseURL: API_URL,
  withCredentials: true, // Cookies httpOnly
  // AIDEV-NOTE: timeout global evita requests pendurados p/ sempre (UI travada em isPending);
  // chamadas longas (upload de foto, import ERP) fazem override por requisição
  timeout: 30_000
});

// AIDEV-NOTE: Controle de refresh em andamento — serializa múltiplas requisições simultâneas
// para que apenas uma chamada a /auth/refresh seja feita, enquanto as demais aguardam na fila.
// Se o refresh falhar, a fila é REJEITADA (nunca descartada) — promise pendurada congela a UI.
let isRefreshing = false;
let refreshQueue: Array<{ resolve: () => void; reject: (err: unknown) => void }> = [];

function drainQueue() {
  refreshQueue.forEach(({ resolve }) => resolve());
  refreshQueue = [];
}

function rejectQueue(err: unknown) {
  refreshQueue.forEach(({ reject }) => reject(err));
  refreshQueue = [];
}

// Interceptor com refresh automático de token
// AIDEV-NOTE: Fluxo: 401 recebido → tenta /auth/refresh → reexecuta original → se refresh falhar → /login
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config as typeof error.config & { _retry?: boolean };
    const isLoginPage = window.location.pathname === '/login';
    const isRefreshCall = originalRequest?.url?.includes('/auth/refresh');
    const isAuthMe = originalRequest?.url?.includes('/auth/me');

    if (error.response?.status === 401 && !isRefreshCall && !originalRequest._retry) {
      originalRequest._retry = true;

      if (isRefreshing) {
        // Aguarda o refresh em andamento e reexecuta a requisição original
        return new Promise<void>((resolve, reject) => {
          refreshQueue.push({ resolve, reject });
        }).then(() => api(originalRequest));
      }

      isRefreshing = true;

      try {
        await api.post('/auth/refresh');
        drainQueue();
        return api(originalRequest);
      } catch (refreshErr) {
        rejectQueue(refreshErr);
        // AIDEV-SECURITY: So desloga se o refresh foi REJEITADO (401/403). Falha de
        // rede/timeout/5xx mantem a sessao — a proxima acao do usuario tenta de novo.
        if (isAuthRejection(refreshErr) && !isLoginPage && !isAuthMe) {
          localStorage.removeItem('user');
          window.location.href = '/login';
        }
        return Promise.reject(error);
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(error);
  }
);

export interface User {
  id: string;
  email: string;
  nome: string;
  vendedor_id: number | null;
  perfil: 'admin' | 'gerente' | 'vendedor' | 'representante';
  client_id: number;
  tabela_preco_padrao: number | null;
  pode_selecionar_tabela: boolean;
  apenas_proprio: boolean;
  gerente_id: number | null;
  // Policy fields — populated from JWT claims after login
  pode_dar_desconto_item: boolean;
  limite_desconto_item: number;
  pode_dar_desconto_total: boolean;
  limite_desconto_total: number;
  pode_cadastrar_clientes: boolean;
  pode_alterar_origem_pedido: boolean;
  pode_alterar_empresa: boolean;
  origem_pedido_padrao_id: number | null;
  empresa_padrao_id: number | null;
  tipo_comercializacao_padrao_id: number | null;
  tipo_movimento_receber_padrao_id: number | null;
  pode_alterar_tipo_movimento_receber: boolean;
  config_sync_pedido: 'automatico' | 'manual';
  exibir_estoque_vendedor: boolean;
  regras_tabela_quantidade: Array<{
    tabela_preco_id: number;
    quantidade_minima: number;
    quantidade_maxima: number | null;
    ordem: number;
  }>;
  restricoes_condicao_pagto_jwt: Array<{
    condicao_pagto_id: number;
    ativo: boolean;
    valor_minimo_pedido: number | null;
  }>;
  // AIDEV-NOTE: Campos do nível 5 do PricingService — controlam tipo de preço base e regras por quantidade.
  // Populados pelo backend em auth.routes.ts a partir de politicas_padrao + regras_tabela_preco_quantidade.
  tipo_preco_base: 'V' | 'A';
  tem_regras_quantidade: boolean;
  // AIDEV-NOTE: Campos de controle de preço unitário — controlam se vendedor pode alterar e os limites de redução/aumento.
  pode_alterar_preco_unitario: boolean;
  limite_reducao_preco_pct: number | null;
  limite_aumento_preco_pct: number | null;
}

export interface LoginResponse {
  usuario: User;
  expiresIn: number;
  tokenType: 'Bearer';
}

export interface LoginInput {
  email: string;
  senha: string;
  lembrar?: boolean;
}

/**
 * Serviço de Autenticação
 */
export const authService = {
  /**
   * Login
   */
  async login(email: string, senha: string): Promise<LoginResponse> {
    const response = await api.post<LoginResponse>('/auth/login', {
      email,
      senha
    });
    return response.data;
  },

  /**
   * Logout
   */
  async logout(): Promise<void> {
    await api.post('/auth/logout');
  },

  /**
   * Refresh token
   */
  async refreshToken(): Promise<{ accessToken: string; expiresIn: number }> {
    const response = await api.post('/auth/refresh');
    return response.data;
  },

  /**
   * Busca usuário atual
   */
  // AIDEV-NOTE: getCurrentUser aplica defaults para campos de políticas comerciais
  // garantindo retrocompatibilidade caso o backend ainda não envie esses campos.
  async getCurrentUser(): Promise<User | null> {
    try {
      const response = await api.get<{ usuario: Record<string, unknown> }>('/auth/me');
      const u = response.data.usuario;
      return {
        ...u,
        pode_dar_desconto_item: u.pode_dar_desconto_item ?? false,
        limite_desconto_item: u.limite_desconto_item ?? 0,
        pode_dar_desconto_total: u.pode_dar_desconto_total ?? false,
        limite_desconto_total: u.limite_desconto_total ?? 0,
        pode_cadastrar_clientes: u.pode_cadastrar_clientes ?? false,
        pode_alterar_origem_pedido: u.pode_alterar_origem_pedido ?? true,
        pode_alterar_empresa: u.pode_alterar_empresa ?? true,
        origem_pedido_padrao_id: u.origem_pedido_padrao_id ?? null,
        empresa_padrao_id: u.empresa_padrao_id ?? null,
        tipo_comercializacao_padrao_id: u.tipo_comercializacao_padrao_id ?? null,
        tipo_movimento_receber_padrao_id: u.tipo_movimento_receber_padrao_id ?? null,
        pode_alterar_tipo_movimento_receber: u.pode_alterar_tipo_movimento_receber ?? true,
        config_sync_pedido: u.config_sync_pedido ?? 'manual',
        exibir_estoque_vendedor: u.exibir_estoque_vendedor ?? true,
        regras_tabela_quantidade: u.regras_tabela_quantidade ?? [],
        restricoes_condicao_pagto_jwt: u.restricoes_condicao_pagto_jwt ?? [],
        // AIDEV-NOTE: defaults defensivos para nível 5 do PricingService — backend sempre envia, mas garante retrocompat.
        tipo_preco_base: (u.tipo_preco_base ?? 'V') as 'V' | 'A',
        tem_regras_quantidade: u.tem_regras_quantidade ?? false,
        // AIDEV-NOTE: defaults para controle de preço unitário
        pode_alterar_preco_unitario: u.pode_alterar_preco_unitario ?? false,
        limite_reducao_preco_pct: u.limite_reducao_preco_pct ?? null,
        limite_aumento_preco_pct: u.limite_aumento_preco_pct ?? null
      } as User;
    } catch {
      return null;
    }
  },

  /**
   * Verifica se está autenticado
   */
  isAuthenticated(): boolean {
    return !!localStorage.getItem('user');
  }
};

export default api;
